import { App } from "@/App";
import { Button } from "@/components/Button/Button";
import { RefreshSvg } from "@/components/Icon/Icon";
import { PH_NO_CAPTURE, POSTHOG_VISIBLE_KEY } from "@/constants/posthog";
import { store } from "@/store/store";
import { theme } from "@/themes/theme";
import { getCookieConsentState, initServicesForConsent } from "@/utils/cookieConsent";
import { ThemeProvider } from "@emotion/react";
import { Box, CssBaseline, Typography } from "@mui/material";
import { PostHogProvider } from "@posthog/react";
import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";

const existingConsent = getCookieConsentState();
if (existingConsent) {
  initServicesForConsent(existingConsent);
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

const options = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  defaults: "2026-01-30",
  opt_out_capturing_by_default: true,
  opt_out_persistence_by_default: true,
  // Mask all text elements, excluding those with data-ph-visible="true".
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: "*",
    blockClass: PH_NO_CAPTURE,
    maskTextFn: (text: string, element: HTMLElement | undefined) => {
      if (element?.dataset[POSTHOG_VISIBLE_KEY] === "true") {
        return text;
      }
      return "*".repeat(text.trim().length);
    }
  }
} as const;

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <Sentry.ErrorBoundary
          fallback={({ resetError }) => {
            return (
              <Box
                component="div"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%"
                }}
              >
                <Button startIcon={<RefreshSvg color="inherit" />} onClick={() => resetError()}>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <Typography>Click here to reset!</Typography>
                </Button>
              </Box>
            );
          }}
          showDialog
        >
          <PostHogProvider apiKey={import.meta.env.VITE_POSTHOG_TOKEN} options={options}>
            <App />
          </PostHogProvider>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </Provider>
  </React.StrictMode>
);
